import { AsyncPipe, DatePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, interval, map, mergeMap, of, startWith } from 'rxjs';

import { environment } from '../core/environments/environments';
import { CurrencyExchangeService } from '../core/services/currency-exchange.service';
import { computePygPerUnit, formatPygPtBr } from '../core/utils/pyg-quotation-format';

@Component({
  selector: 'app-quotation-panel',
  standalone: true,
  imports: [AsyncPipe, DatePipe],
  templateUrl: './quotation-panel.component.html',
  styleUrl: './quotation-panel.component.scss',
})
export class QuotationPanelComponent {
  readonly #api = inject(CurrencyExchangeService);

  protected readonly pollMs = environment.pollIntervalMs;

  protected readonly vm$ = interval(this.pollMs).pipe(
    startWith(0),
    mergeMap(() =>
      this.#api.fetchCurrencyExchange().pipe(
        map((res) => {
          if (!res?.length) {
            return { brl: '—', usd: '—', error: 'Resposta inválida da API.', updatedAt: null as Date | null };
          }

          const latestQuotation = res.reduce((latest, current) =>
            new Date(current.lastUpdate).getTime() > new Date(latest.lastUpdate).getTime() ? current : latest
          );

          const brlVal = computePygPerUnit(String(latestQuotation.realPrice));
          const usdVal = computePygPerUnit(String(latestQuotation.dolarPrice));
          const ts = new Date(latestQuotation.lastUpdate);

          return {
            brl: formatPygPtBr(brlVal),
            usd: formatPygPtBr(usdVal),
            error: null as string | null,
            updatedAt: Number.isNaN(ts.getTime()) ? new Date() : ts,
          };
        }),
        catchError((err) =>
          of({
            brl: '—',
            usd: '—',
            error: this.#formatError(err),
            updatedAt: null as Date | null,
          })
        )
      )
    ),
    takeUntilDestroyed()
  );

  #formatError(err: unknown): string {
    if (err instanceof Error) {
      return err.message;
    }
    const status = (err as { status?: number })?.status;
    if (status) {
      return `Falha ao obter cotações (HTTP ${status}).`;
    }
    return 'Falha ao obter cotações.';
  }
}
